import { useCallback, useEffect, useState } from "react";

import {
  listOrganisationInvites,
  resendOrganisationInvite,
  revokeOrganisationInvite,
} from "../api/officers";

const ROLE_LABELS = {
  ADMIN: "Admin",
  VERIFICATION_OFFICER: "Verification officer",
  MEMBER: "Member",
};

function formatDay(iso) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * The organisation's invitations still waiting to be accepted, for its
 * admins: send one again (a new link for 72 hours) or withdraw it. Renders
 * nothing while there are none. `refreshKey` changes when a new invitation
 * was sent from the page.
 */
export function OrganisationInvitations({ organisationId, refreshKey }) {
  const [invites, setInvites] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setInvites(await listOrganisationInvites(organisationId));
      setError(null);
    } catch (err) {
      setError(err.message ?? "The invitations couldn't be loaded.");
    }
  }, [organisationId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  async function act(invite, work, success) {
    setBusyId(invite.id);
    setNotice(null);
    setError(null);
    try {
      await work();
      setNotice(success);
      await load();
    } catch (err) {
      setError(err.message ?? "That didn't work. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  function withdraw(invite) {
    if (
      !window.confirm(
        `Withdraw the invitation to ${invite.invitedEmail}? Their link stops working.`,
      )
    ) {
      return;
    }
    act(
      invite,
      () => revokeOrganisationInvite(organisationId, invite.id),
      `The invitation to ${invite.invitedEmail} was withdrawn.`,
    );
  }

  if (!invites?.length && !error) return null;

  return (
    <section
      aria-labelledby="waiting-invitations"
      className="mt-6 rounded-xl border border-slate-200 bg-white"
    >
      <div className="px-6 py-4 border-b border-slate-100">
        <h2
          id="waiting-invitations"
          className="text-sm font-bold text-slate-900"
        >
          Invitations waiting
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          People you invited who have not set up their account yet. A link works
          for 72 hours.
        </p>
      </div>
      {error && (
        <p className="px-6 pt-3 text-xs font-medium text-red-700">{error}</p>
      )}
      {notice && (
        <p className="px-6 pt-3 text-xs font-medium text-emerald-700">
          {notice}
        </p>
      )}
      <ul className="divide-y divide-slate-100">
        {(invites ?? []).map((invite) => (
          <li
            key={invite.id}
            className="px-6 py-3 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 break-words">
                {invite.fullName || invite.invitedEmail}
              </p>
              <p className="text-xs text-slate-500 break-words">
                {[
                  invite.fullName ? invite.invitedEmail : null,
                  ROLE_LABELS[invite.role] ?? invite.role,
                  invite.branch?.name,
                  invite.invitedBy ? `invited by ${invite.invitedBy}` : null,
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </p>
              <p
                className={`text-xs ${invite.expired ? "text-red-700" : "text-slate-500"}`}
              >
                {invite.expired
                  ? "The link has expired. Send it again."
                  : `Link works until ${formatDay(invite.expiresAt)}`}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busyId === invite.id}
                onClick={() =>
                  act(
                    invite,
                    () => resendOrganisationInvite(organisationId, invite.id),
                    `A new link was emailed to ${invite.invitedEmail}.`,
                  )
                }
                className="px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Send again
              </button>
              <button
                type="button"
                disabled={busyId === invite.id}
                onClick={() => withdraw(invite)}
                className="px-3 py-1.5 text-xs font-semibold rounded border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Withdraw
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
